import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';
import { DataTableModuleConfiguration } from '../data-table/datatable-module.config';
import { ACTING_ORGANIZATION_ID_PROVIDER } from './current-organization-id.provider';

export interface FieldInfo {
  key: string;
  inferredType: string;
  occurrenceCount: number;
  sampleValues: string[];
}

export interface ResultFieldsResponse {
  fields: FieldInfo[];
  sampleSize: number;
}

interface CachedResultFieldsResponse {
  value: ResultFieldsResponse;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class ResultFieldsService {
  private http = inject(HttpClient);
  private configuration = inject(DataTableModuleConfiguration);
  private getCurrentOrganizationId = inject(ACTING_ORGANIZATION_ID_PROVIDER);
  private readonly cacheTtlMs = 2 * 60 * 1000;
  private readonly allConnectionsCacheKey = '__all__';
  private readonly noOrganizationCacheKey = '__no_org__';

  private resultFieldsCache = new Map<string, CachedResultFieldsResponse>();
  private dataFieldsCache = new Map<string, CachedResultFieldsResponse>();
  private inFlightResultRequests = new Map<
    string,
    Observable<ResultFieldsResponse>
  >();
  private inFlightDataRequests = new Map<
    string,
    Observable<ResultFieldsResponse>
  >();

  private get baseUrl(): string {
    return `${this.configuration.apiUrls.workflow}/api/v1/dashboard/jobs/results`;
  }

  /**
   * Discovers available result fields from job results.
   * @param dataConnectionIds Optional filter by data connection IDs
   */
  discoverFields(
    dataConnectionIds?: string[],
  ): Observable<ResultFieldsResponse> {
    return this.fetchFieldsWithCache(
      `${this.baseUrl}/fields`,
      dataConnectionIds,
      this.resultFieldsCache,
      this.inFlightResultRequests,
    );
  }

  /**
   * Discovers available data fields from job input data.
   * @param dataConnectionIds Optional filter by data connection IDs
   */
  discoverDataFields(
    dataConnectionIds?: string[],
  ): Observable<ResultFieldsResponse> {
    return this.fetchFieldsWithCache(
      `${this.configuration.apiUrls.workflow}/api/v1/dashboard/jobs/data/fields`,
      dataConnectionIds,
      this.dataFieldsCache,
      this.inFlightDataRequests,
    );
  }

  clearFieldCaches(): void {
    this.resultFieldsCache.clear();
    this.dataFieldsCache.clear();
    this.inFlightResultRequests.clear();
    this.inFlightDataRequests.clear();
  }

  private fetchFieldsWithCache(
    endpoint: string,
    dataConnectionIds: string[] | undefined,
    cache: Map<string, CachedResultFieldsResponse>,
    inFlight: Map<string, Observable<ResultFieldsResponse>>,
  ): Observable<ResultFieldsResponse> {
    const normalizedDataConnectionIds =
      this.normalizeDataConnectionIds(dataConnectionIds);
    const cacheKey = this.createCacheKey(normalizedDataConnectionIds);
    const now = Date.now();
    const cached = cache.get(cacheKey);

    if (cached && cached.expiresAt > now) {
      return of(cached.value);
    }

    if (cached && cached.expiresAt <= now) {
      cache.delete(cacheKey);
    }

    const existingRequest = inFlight.get(cacheKey);
    if (existingRequest) {
      return existingRequest;
    }

    const request$ = this.http
      .post<ResultFieldsResponse>(endpoint, {
        dataConnectionIds:
          normalizedDataConnectionIds.length > 0
            ? normalizedDataConnectionIds
            : undefined,
      })
      .pipe(
        tap((response) => {
          cache.set(cacheKey, {
            value: response,
            expiresAt: Date.now() + this.cacheTtlMs,
          });
        }),
        finalize(() => inFlight.delete(cacheKey)),
        shareReplay(1),
      );

    inFlight.set(cacheKey, request$);
    return request$;
  }

  private createCacheKey(dataConnectionIds: string[]): string {
    const organizationCacheKey = this.getOrganizationCacheKey();
    if (dataConnectionIds.length === 0) {
      return `${organizationCacheKey}::${this.allConnectionsCacheKey}`;
    }
    return `${organizationCacheKey}::${dataConnectionIds.join(',')}`;
  }

  private getOrganizationCacheKey(): string {
    return this.getCurrentOrganizationId() ?? this.noOrganizationCacheKey;
  }

  private normalizeDataConnectionIds(dataConnectionIds?: string[]): string[] {
    if (!dataConnectionIds || dataConnectionIds.length === 0) {
      return [];
    }

    return [...new Set(dataConnectionIds)]
      .map((id) => id.trim())
      .filter((id): id is string => id.length > 0)
      .sort((a, b) => a.localeCompare(b));
  }
}
